/**
 * BaseModal Styles
 * Styles for the BaseModal component
 */

import { StyleSheet, Platform, type ViewStyle } from 'react-native';

export const baseModalStyles = StyleSheet.create({
  /**
   * In-app full-screen layer for `renderInline`. Blocking prompts must sit above every in-app layer —
   * the floating create button uses 10001 and the highest in the app is 99999 — and iOS flattens
   * layout-only views, so a nested zIndex competes with this one directly.
   */
  inlineLayer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1_000_000,
    elevation: 1_000_000,
  },
  /** Full-screen backdrop container */
  backdrop: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      position: 'fixed' as never,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      zIndex: 1000,
    }),
  },
  /** Invisible full-screen touchable; only this layer closes on press (content is a sibling on top) */
  backdropTouchable: {
    ...StyleSheet.absoluteFillObject,
    ...(Platform.OS === 'web' && {
      cursor: 'default' as never,
    }),
  },
  container: {
    flex: 1,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as never,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      zIndex: 1000,
    }),
  },
  blurContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentContainer: {
    // Content container styles are provided via props
    // This is just a base for positioning
    // Ensure it doesn't collapse when no contentPosition is provided
    ...(Platform.OS === 'web' && {
      position: 'relative' as never,
      zIndex: 1001,
    }),
    // Ensure content is visible even without explicit positioning
    alignSelf: 'center',
  },
});

/**
 * Get backdrop container style with dynamic alignment
 * Returns styles for the backdrop container (used for both web and native)
 */
export const getBackdropContainerStyle = (
  contentAlignment: 'center' | 'flex-start' | 'flex-end',
  contentPosition: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  } | undefined,
  backdropColor: string,
  backdropStyle?: ViewStyle
): ViewStyle => {
  const isFullScreenPosition =
    contentPosition != null &&
    contentPosition.top === 0 &&
    contentPosition.right === 0 &&
    contentPosition.bottom === 0 &&
    contentPosition.left === 0

  return {
    ...baseModalStyles.container,
    justifyContent: contentAlignment,
    // Full-viewport overlays (e.g. drawers): avoid treating `right: 0` as "align to end",
    // which can misplace layers when fixed positioning falls back to a constrained ancestor.
    alignItems: isFullScreenPosition
      ? 'flex-start'
      : contentPosition?.right !== undefined || contentPosition?.left !== undefined
        ? contentPosition?.right !== undefined
          ? 'flex-end'
          : 'flex-start'
        : 'center',
    backgroundColor: backdropColor,
    ...backdropStyle,
  };
};

/**
 * Get blur container style with dynamic alignment
 */
export const getBlurContainerStyle = (
  contentAlignment: 'center' | 'flex-start' | 'flex-end',
  contentPosition?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  }
): ViewStyle => {
  const isFullScreenPosition =
    contentPosition != null &&
    contentPosition.top === 0 &&
    contentPosition.right === 0 &&
    contentPosition.bottom === 0 &&
    contentPosition.left === 0

  return {
    ...baseModalStyles.blurContainer,
    justifyContent: contentAlignment,
    alignItems: isFullScreenPosition
      ? 'flex-start'
      : contentPosition?.right !== undefined || contentPosition?.left !== undefined
        ? contentPosition?.right !== undefined
          ? 'flex-end'
          : 'flex-start'
        : 'center',
  };
};

/**
 * Get content container style with dynamic positioning
 */
export const getContentContainerStyle = (
  contentPosition?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  },
  contentContainerStyle?: ViewStyle
): ViewStyle => {
  const isFullScreen =
    contentPosition &&
    contentPosition.top === 0 &&
    contentPosition.right === 0 &&
    contentPosition.bottom === 0 &&
    contentPosition.left === 0;

  return {
    ...baseModalStyles.contentContainer,
    ...(contentPosition && {
      position: 'absolute' as const,
      top: contentPosition.top,
      right: contentPosition.right,
      bottom: contentPosition.bottom,
      left: contentPosition.left,
    }),
    // Full-screen positioned content needs explicit size so children fill the viewport (web + native).
    ...(isFullScreen && {
      width: '100%' as never,
      height: '100%' as never,
    }),
    // Ensure content container doesn't collapse when no explicit positioning
    ...(!contentPosition && {
      alignSelf: 'center',
    }),
    ...contentContainerStyle,
  };
};